import { zodResolver } from '@hookform/resolvers/zod'
import type { FieldValues, Resolver } from 'react-hook-form'
import type { ZodType, ZodTypeDef } from 'zod'
import i18n from './i18n'

/**
 * `zodResolver` that runs every validation message through i18n.
 *
 * Schemas carry translation keys rather than sentences, but `FormMessage` in
 * the UI package prints whatever string it receives — and it has no access to
 * i18n, since the design system deliberately does not depend on it. Three
 * dialogs worked around that with a private `TranslatedFormMessage`, which
 * only covered the fields they rendered by hand; anything drawn by `TextField`
 * kept showing the raw key.
 *
 * Translating here fixes every form at once, `TextField` included, with no
 * component changes. `i18n.t` returns its input unchanged when no translation
 * exists, so a schema still holding a literal keeps rendering that literal.
 */
export function zodResolverI18n<T extends FieldValues>(
	schema: ZodType<unknown, ZodTypeDef, unknown>,
): Resolver<T> {
	// The installed resolver overloads lead with zod v4 shapes while the project
	// is on v3, so the call needs one cast — kept here rather than at every call
	// site, and widened to accept `.superRefine()` schemas (ZodEffects) too.
	const base = zodResolver(schema as unknown as Parameters<typeof zodResolver>[0]) as Resolver<T>
	return async (values, context, options) => {
		const result = await base(values, context, options)
		if (!result.errors) return result
		for (const key of Object.keys(result.errors)) {
			const error = (result.errors as Record<string, { message?: string }>)[key]
			if (error?.message) error.message = i18n.t(error.message)
		}
		return result
	}
}
